// =====================================================
// TextareaWithAttachments.tsx
// Campo textarea + adjuntar archivos (Cloudinary)
// Para campos como "Exámenes Complementarios"
// =====================================================

import React, { useState, useRef } from 'react';
import {
  Paperclip,
  Upload,
  X,
  FileText,
  Image,
  File,
  Loader2,
  Download,
  Eye,
  AlertCircle
} from 'lucide-react';

interface AttachedFile {
  url: string;
  name: string;
  type: string;
  size: number;
}

interface TextareaWithAttachmentsProps {
  textValue: string;
  filesValue: AttachedFile[];
  onTextChange: (text: string) => void;
  onFilesChange: (files: AttachedFile[]) => void;
  placeholder?: string;
  error?: string;
  label?: string;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const ACCEPTED_TYPES = '.jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx';
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const getFileIcon = (type: string) => {
  if (type.startsWith('image/')) return <Image className="w-4 h-4 text-green-600" />;
  if (type.includes('pdf')) return <FileText className="w-4 h-4 text-red-600" />;
  return <File className="w-4 h-4 text-blue-600" />;
};

const TextareaWithAttachments: React.FC<TextareaWithAttachmentsProps> = ({
  textValue,
  filesValue,
  onTextChange,
  onFilesChange,
  placeholder,
  error,
  label
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tamaño
    if (file.size > MAX_FILE_SIZE) {
      setUploadError('El archivo supera el máximo de 10MB');
      return;
    }

    setUploadError('');
    setUploading(true);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/clinical-records/upload-temp`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        const newFile: AttachedFile = {
          url: data.file.url,
          name: data.file.name,
          type: data.file.type,
          size: data.file.size
        };
        onFilesChange([...filesValue, newFile]);
      } else {
        setUploadError(data.message || 'Error al subir archivo');
      }
    } catch (err) {
      setUploadError('Error de conexión al subir archivo');
    } finally {
      setUploading(false);
      // Reset input
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (index: number) => {
    const updated = filesValue.filter((_, i) => i !== index);
    onFilesChange(updated);
  };

  return (
    <div className="space-y-2">
      {/* Textarea */}
      <div className="relative">
        <textarea
          value={textValue}
          onChange={(e) => onTextChange(e.target.value)}
          placeholder={placeholder}
          rows={4}
          className={`w-full px-4 py-3 bg-gray-50 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
            error ? 'border-red-300 bg-red-50' : 'border-gray-200'
          }`}
        />
      </div>

      {/* Botón adjuntar + archivos */}
      <div className="space-y-2">
        {/* Botón de adjuntar */}
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES}
            onChange={handleFileSelect}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors disabled:opacity-50"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Subiendo...
              </>
            ) : (
              <>
                <Paperclip className="w-4 h-4" />
                Adjuntar archivo
              </>
            )}
          </button>
          
          {filesValue.length > 0 && (
            <span className="text-xs text-gray-500">
              {filesValue.length} archivo{filesValue.length !== 1 ? 's' : ''} adjunto{filesValue.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {/* Error de upload */}
        {uploadError && (
          <div className="flex items-center gap-1.5 text-sm text-red-600">
            <AlertCircle className="w-4 h-4" />
            {uploadError}
          </div>
        )}

        {/* Lista de archivos adjuntos */}
        {filesValue.length > 0 && (
          <div className="space-y-1.5">
            {filesValue.map((file, index) => (
              <div
                key={index}
                className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200 group"
              >
                {getFileIcon(file.type)}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-700 truncate">{file.name}</p>
                  <p className="text-xs text-gray-400">{formatFileSize(file.size)}</p>
                </div>
                <div className="flex items-center gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                  {file.type.startsWith('image/') && (
                    <button
                      type="button"
                      onClick={() => setPreviewUrl(file.url)}
                      className="p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      title="Ver imagen"
                    >
                      <Eye className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <a
                    href={file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                    title="Descargar"
                  >
                    <Download className="w-3.5 h-3.5" />
                  </a>
                  <button
                    type="button"
                    onClick={() => handleRemoveFile(index)}
                    className="p-1 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                    title="Quitar"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal preview de imagen */}
      {previewUrl && (
        <div 
          className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setPreviewUrl(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            <button
              type="button"
              onClick={() => setPreviewUrl(null)}
              className="absolute -top-3 -right-3 p-1.5 bg-white rounded-full shadow-lg text-gray-700 hover:text-red-600 z-10"
            >
              <X className="w-5 h-5" />
            </button>
            <img
              src={previewUrl}
              alt="Preview"
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default TextareaWithAttachments;